import React, { useState, useMemo } from "react";
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Autocomplete,
  IconButton,
  Paper,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import SearchIcon from "@mui/icons-material/Search";
import { CategoryIconBox, theme } from "../../styles/CategoriaStyles";
import { getCategoriaIcon } from "./IconHelper";
import { filtrarRepresentantesParaBusqueda } from "../../utils/representanteUtils";

const SearchDrawer = ({ 
  open, 
  onClose, 
  representantes = [], 
  categorias = [],
  asociaciones = [] // Agregar asociaciones como prop
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRepresentante, setSelectedRepresentante] = useState(null);

  // Usar useMemo para evitar recalcular en cada render
  const filteredRepresentantes = useMemo(() => {
    return filtrarRepresentantesParaBusqueda(representantes, searchTerm);
  }, [representantes, searchTerm]);

  const handleClose = () => {
    onClose();
    setSearchTerm("");
    setSelectedRepresentante(null);
  };

  const handleInputChange = (event, newInputValue) => {
    setSearchTerm(newInputValue);
    // Si empieza a escribir, limpiar selección anterior
    if (newInputValue && selectedRepresentante) {
      setSelectedRepresentante(null);
    }
  };

  const handleSelectionChange = (event, newValue) => {
    setSelectedRepresentante(newValue);
  };

  // Función para obtener el nombre de la asociación
  const getNombreAsociacion = (cod_asociacion) => {
    const asociacion = asociaciones.find(
      (a) => a.cod_asociacion === cod_asociacion
    );
    return asociacion ? asociacion.nombre_abreviado : "Sin asociación";
  };

  return (
    <Drawer 
      anchor="right" 
      open={open} 
      onClose={handleClose}
      PaperProps={{ 
        sx: { 
          width: { xs: '100%', sm: 400 }, 
          p: 3 
        } 
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <PersonSearchIcon sx={{ 
          fontSize: 28, 
          color: theme.palette.primary.main, 
          mr: 2 
        }} />
        <Typography variant="h5" sx={{ 
          color: theme.palette.text.primary, 
          fontWeight: 700, 
          flexGrow: 1 
        }}>
          Buscar Representante
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Search Input */}
      <Autocomplete
        options={filteredRepresentantes}
        getOptionLabel={(option) => `${option.nombre_representante} - ${option.dni}`}
        value={selectedRepresentante}
        onChange={handleSelectionChange}
        onInputChange={handleInputChange}
        inputValue={searchTerm}
        // CRÍTICO: Deshabilitar el filtrado interno del Autocomplete
        filterOptions={(x) => x}
        // No cerrar al hacer blur
        clearOnBlur={false}
        renderInput={(params) => (
          <TextField 
            {...params} 
            label="Buscar Representante" 
            placeholder="Nombre o DNI..." 
            fullWidth
            helperText={searchTerm.length > 0 && searchTerm.length < 2 ? "Escribe al menos 2 caracteres" : " "}
            InputProps={{ 
              ...params.InputProps, 
              startAdornment: (
                <SearchIcon sx={{ 
                  color: theme.palette.primary.main, 
                  mr: 1 
                }} />
              )
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Avatar sx={{ 
              backgroundColor: theme.palette.primary.main, 
              width: 32, 
              height: 32, 
              mr: 2 
            }}>
              {option.nombre_representante?.charAt(0) || "R"}
            </Avatar>
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {option.nombre_representante}
              </Typography>
              <Typography variant="caption" sx={{ 
                color: theme.palette.text.secondary 
              }}>
                {option.dni} • {(option.categorias || []).filter(cat => cat.Estado !== 'Inactivo').length} categoría(s)
              </Typography>
            </Box>
          </Box>
        )}
        noOptionsText={
          searchTerm.length < 2 
            ? "Escribe al menos 2 caracteres" 
            : "No se encontraron representantes"
        }
        loadingText="Buscando..."
      />

      {/* Selected Representative Details */}
      {selectedRepresentante && (
        <Box sx={{ mt: 4 }}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              backgroundColor: theme.palette.background.card, 
              border: `1px solid ${theme.palette.border.main}`, 
              borderRadius: "12px" 
            }}
          >
            {/* Representative Header */}
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Avatar 
                src={selectedRepresentante.fotografia || ""} 
                sx={{ 
                  width: 80, 
                  height: 80, 
                  mx: "auto", 
                  mb: 2, 
                  border: `3px solid ${theme.palette.primary.main}` 
                }}
              >
                {selectedRepresentante.nombre_representante?.charAt(0) || "R"}
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {selectedRepresentante.nombre_representante}
              </Typography>
              <Typography variant="body2" sx={{ 
                color: theme.palette.text.secondary 
              }}>
                DNI: {selectedRepresentante.dni}
              </Typography>
            </Box>

            <Divider sx={{ mb: 2 }} />
            
            {/* Información de la Asociación */}
            <Box sx={{ mb: 3, p: 2, backgroundColor: '#f8fafc', borderRadius: '8px' }}>
              <Typography variant="caption" sx={{ 
                color: theme.palette.text.secondary,
                textTransform: 'uppercase',
                fontWeight: 600,
                letterSpacing: '0.5px'
              }}>
                Asociación
              </Typography>
              <Typography variant="body1" sx={{ 
                fontWeight: 700,
                color: theme.palette.primary.main,
                mt: 0.5
              }}>
                {getNombreAsociacion(selectedRepresentante.cod_asociacion)}
              </Typography>
            </Box>
            
            <Typography variant="subtitle2" sx={{ 
              mb: 2, 
              fontWeight: 700 
            }}>
              Representante en
            </Typography>

            {/* Categories List */}
            {(() => {
              const categoriasActivas = (selectedRepresentante.categorias || []).filter(cat => cat.Estado !== 'Inactivo');
              return (
                <List>
                  {categoriasActivas.map((cat, index) => {
                    const categoria = categorias.find((c) => c.Id === cat.Id);
                    return (
                      <React.Fragment key={index}>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemAvatar>
                            <CategoryIconBox>
                              {getCategoriaIcon(categoria?.Descripcion, 20, theme)}
                            </CategoryIconBox>
                          </ListItemAvatar>
                          <ListItemText
                            primary={categoria?.Descripcion || "Sin categoría"}
                            secondary={cat.Cargo || "Sin cargo"}
                            primaryTypographyProps={{
                              variant: "body1",
                              fontWeight: 600
                            }}
                            secondaryTypographyProps={{
                              variant: "body2",
                              color: theme.palette.text.secondary
                            }}
                          />
                        </ListItem>
                        {index < categoriasActivas.length - 1 && (
                          <Divider />
                        )}
                      </React.Fragment>
                    );
                  })}
                </List>
              );
            })()}

            {/* Additional Info */}
            {(selectedRepresentante.categorias || []).filter(cat => cat.Estado !== 'Inactivo').length === 0 && (
              <Box sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="body2" sx={{ 
                  color: theme.palette.text.secondary 
                }}>
                  Este representante no tiene categorías asignadas
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {!searchTerm && !selectedRepresentante && (
        <Box sx={{ textAlign: "center", mt: 6, py: 4 }}>
          <PersonSearchIcon sx={{ 
            fontSize: 60, 
            color: theme.palette.border.light, 
            mb: 2 
          }} />
          <Typography variant="h6" sx={{ 
            color: theme.palette.text.secondary, 
            mb: 1 
          }}>
            Buscar Representante
          </Typography>
          <Typography variant="body2" sx={{ 
            color: theme.palette.text.secondary,
            maxWidth: 250,
            mx: "auto"
          }}>
            Escribe el nombre o DNI del representante que deseas encontrar
          </Typography>
        </Box>
      )}
    </Drawer>
  );
};

export default SearchDrawer;